// src/utils/request.js
import axios from 'axios'
import { showToast, showLoadingToast, closeToast } from 'vant'
import router from '@/router'

// 请求计数器
let requestCount = 0

// 显示加载提示
const showLoading = () => {
  if (requestCount === 0) {
    showLoadingToast({
      message: '加载中...',
      forbidClick: true,
      duration: 0
    })
  }
  requestCount++
}
// 隐藏加载提示
const hideLoading = () => {
  requestCount--
  if (requestCount === 0) {
    closeToast()
  }
}


// 创建 axios 实例
const service = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  },
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 不显示加载提示的请求配置
    if (!config.noLoading) {
      showLoading()
    }

    // 从 localStorage 获取 token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  error => {
    hideLoading()
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 如果请求没有配置 noLoading，则隐藏加载提示
    if (!response.config.noLoading) {
      hideLoading()
    }
    
    const res = response.data
    // console.log(res);
    
    // 修改判断逻辑，处理所有 2xx 状态码
    if (res.code >= 200 && res.code < 300) {
      // 如果需要显示成功提示
      if (res.message && !response.config.noSuccessMsg) {
        // showToast({
        //   message: res.message,
        //   type: 'success'
        // })
      }
      return res.data
    }

    // 统一错误提示
    const errorMsg = res.message || (res.errors?.[0]?.msg) || '请求失败'
    showToast({
      message: errorMsg,
      type: 'error'
    })

    // 401 处理
    if (res.code === 401) {
      localStorage.removeItem('token')
      router.push('/login')
    }

    return Promise.reject(new Error(errorMsg))
  },
  error => {
    // 隐藏加载提示
    if (!error.config.noLoading) {
      hideLoading()
    }
    // 处理请求超时
    if (error.code === 'ECONNABORTED' && error.message.indexOf('timeout') !== -1) {
      showToast({
        message: '请求超时，请重试',
        type: 'error'
      })
      return Promise.reject(error)
    }
    // 处理网络错误
    if (!error.response) {
      showToast({
        message: '网络异常，请检查网络连接',
        type: 'error'
      })
      return Promise.reject(error)
    }
    // 处理 HTTP 状态码错误
    const status = error.response.status
    if (status === 401) {
      // 未授权，清除用户信息并跳转到登录页
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      router.push('/login')
      showToast({
        message: '登录已过期，请重新登录',
        type: 'error'
      })
    } else if (status === 500) {
      showToast({
        message: '服务器错误，请稍后重试',
        type: 'error'
      })
    } else {
      // 其他错误
      const errorMsg = error.response.data?.message || '请求失败，请重试'
      showToast({
        message: errorMsg,
        type: 'error'
      })
    }

    return Promise.reject(error)
  }
)
// 封装请求方法
export const request = {
  get(url, params = null, config = {}) {
    return service.get(url, {
      ...config,
      params
    })
  },
  post(url, data = null, config = {}) {
    return service.post(url, data, config)
  },
  put(url, data = null, config = {}) {
    return service.put(url, data, config)
  },
  patch(url, data = null, config = {}) {
    return service.patch(url, data, config)
  },
  delete(url, params = null, config = {}) {
    return service.delete(url, {
      ...config,
      params
    })
  },

  // 上传文件
  upload(url, file, config = {}) {
    const formData = new FormData()
    formData.append('image', file)

    return service.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      ...config
    })
  },

  // 批量上传文件
  uploadMultiple(url, files, config = {}) {
    const formData = new FormData();

    // 这里是关键：保持和 Postman 一样的字段名
    files.forEach(file => {
      formData.append('images', file);
    });
    return service.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      transformRequest: [(data) => data], // 防止 axios 转换 FormData
      ...config
    });
  },

  // 下载文件
  download(url, params, config = {}) {
    return service.get(url, {
      params,
      responseType: 'blob',
      ...config
    })
  }
}

export default request